export type VersionInfo = {
  version: string;
  time?: string;
  name?: string;
};

const VERSION_URL = "/version.json";

function getCurrentBuildVersion(): string | undefined {
  // Value from .env baked into bundle at build time
  return (process.env.REACT_APP_BUILD_VERSION as string) || undefined;
}

/** Same deploy = same pkg+git; trailing `.timestamp` from write-version can differ from bundle. */
function comparableVersion(v: string): string {
  return v.replace(/\.\d{10,}$/, "");
}

async function fetchVersionInfo(cacheBust: boolean): Promise<VersionInfo | null> {
  try {
    const url = cacheBust ? `${VERSION_URL}?t=${Date.now()}` : VERSION_URL;
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as VersionInfo;
  } catch {
    return null;
  }
}

export function startVersionWatcher(options?: {
  intervalMs?: number;
  onUpdateFound?: (remote: VersionInfo, localVersion: string | undefined) => void;
  autoReload?: boolean;
}): () => void {
  const intervalMs = options?.intervalMs ?? 60_000; // default 1 min
  const autoReload =
    options?.autoReload ?? process.env.NODE_ENV === "production";
  const localVersion = getCurrentBuildVersion();

  let stopped = false;
  let timer: number | undefined;

  const check = async () => {
    if (stopped) return;
    const remote = await fetchVersionInfo(true);
    if (!remote) return;

    if (
      remote.version &&
      localVersion &&
      comparableVersion(remote.version) !== comparableVersion(localVersion)
    ) {
      options?.onUpdateFound?.(remote, localVersion);
      if (autoReload) {
        // Bypass caches thoroughly
        if ("serviceWorker" in navigator) {
          // Try to unregister SW first to avoid stale assets
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister().catch(() => undefined)));
        }
        window.location.reload();
      }
    }
  };

  // Initial delayed check to allow app to mount
  timer = window.setInterval(check, intervalMs);
  // Also fire one soon after startup
  setTimeout(check, 5_000);

  return () => {
    stopped = true;
    if (timer) window.clearInterval(timer);
  };
}


